import { CheckIcon, CopyIcon, GearIcon, SpinnerIcon, TrashIcon } from '@phosphor-icons/react';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorState } from '@/components/error-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useGitBaseUrl } from '@/hooks/use-org';
import { BUILD_SHA } from '@/lib/build-stamp';
import { pushError } from '@/lib/faro';
import { listOrgsQueryOptions, orgBySlugQueryOptions } from '@/lib/org-query-options';
import { deleteOrgServerFn, syncActiveOrg, updateOrgServerFn } from '@/server/functions/organization';
import { loadSettingsPage } from '@/server/functions/pages/settings';

function SettingsSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <Skeleton className="h-7 w-28" />
      </div>

      <div className="border border-mesa-border p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <Skeleton className="h-6 w-40 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="h-9 w-44" />
        </div>
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-mesa-border">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="h-8 w-12 mb-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </div>

      <div className="border border-mesa-border/30 p-6 space-y-4">
        <div className="space-y-1">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-9 w-44" />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/$orgSlug/settings')({
  ssr: false,
  loader: async ({ context }) => {
    return context.queryClient.ensureQueryData({
      queryKey: ['settings-page', context.org.slug],
      queryFn: () => loadSettingsPage({ data: { orgSlug: context.org.slug } }),
    });
  },
  pendingComponent: SettingsSkeleton,
  errorComponent: ErrorState,
  component: SettingsPage,
});

function SettingsPage() {
  const navigate = useNavigate();
  const { org: routeOrg } = Route.useRouteContext();
  const { orgSlug } = Route.useParams();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;
  const gitBaseUrl = useGitBaseUrl();
  const queryClient = useQueryClient();
  const { data: allOrgs, refetch: refetchAllOrgs } = useQuery(listOrgsQueryOptions);

  const pageData = Route.useLoaderData();
  const repoCount = pageData.repoCount;
  const memberCount = pageData.memberCount;
  const tokenCount = pageData.apiKeyCount;

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [buildCopied, setBuildCopied] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [orgNameValue, setOrgNameValue] = useState('');
  const [deleteConfirmValue, setDeleteConfirmValue] = useState('');

  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleRenameOrg = async () => {
    if (!orgNameValue.trim() || orgNameValue.trim() === org.name) return;

    setIsRenaming(true);
    setRenameError(null);

    try {
      await updateOrgServerFn({ data: { orgSlug: org.slug, name: orgNameValue.trim() } });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth-orgs'] }),
        queryClient.invalidateQueries({ queryKey: ['auth-org'] }),
      ]);
      setIsRenameDialogOpen(false);
    } catch (err) {
      pushError(err);
      setRenameError(err instanceof Error ? err.message : 'Failed to rename organization');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (deleteConfirmValue !== org.name) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteOrgServerFn({ data: { orgSlug: org.slug } });

      const otherOrgs = (allOrgs ?? []).filter((o) => o.id !== org.id);
      if (otherOrgs.length > 0) {
        await syncActiveOrg({ data: { orgId: otherOrgs[0].id } });
        await navigate({ to: '/$orgSlug/tokens', params: { orgSlug: otherOrgs[0].slug } });
      } else {
        await refetchAllOrgs();
        await navigate({ to: '/onboarding' });
      }
    } catch (err) {
      pushError(err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete organization');
      setIsDeleting(false);
    }
  };

  const handleOpenRename = () => {
    setOrgNameValue(org.name ?? '');
    setRenameError(null);
    setIsRenameDialogOpen(true);
  };

  const handleCloseRenameDialog = () => {
    setIsRenameDialogOpen(false);
    setOrgNameValue('');
    setRenameError(null);
  };

  const handleOpenDelete = () => {
    setDeleteConfirmValue('');
    setDeleteError(null);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteConfirmValue('');
    setDeleteError(null);
  };

  const canRename = orgNameValue.trim() && orgNameValue.trim() !== org.name;

  const baseUrlWithoutProtocol = gitBaseUrl.replace(/^https?:\/\//, '');
  const orgUrl = `${baseUrlWithoutProtocol}/${org.slug}`;

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(orgUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const handleCopyBuild = () => {
    if (!BUILD_SHA) return;
    navigator.clipboard.writeText(BUILD_SHA);
    setBuildCopied(true);
    setTimeout(() => setBuildCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <GearIcon />
          Settings
        </h1>
      </div>

      <div className="border border-mesa-border p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-lg font-medium">{org.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-sm text-mesa-muted bg-mesa-cream-dark px-2 py-0.5 rounded">{orgUrl}</code>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopyUrl}>
                {urlCopied ? (
                  <CheckIcon className="h-3.5 w-3.5 text-green-600" weight="fill" />
                ) : (
                  <CopyIcon className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
          <Button variant="outline" onClick={handleOpenRename}>
            Update display name
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-mesa-border">
          <div>
            <p className="text-2xl font-semibold">{memberCount}</p>
            <p className="text-sm text-mesa-muted">Members</p>
          </div>
          <div>
            <p className="text-2xl font-semibold">{repoCount}</p>
            <p className="text-sm text-mesa-muted">Repositories</p>
          </div>
          <div>
            <p className="text-2xl font-semibold">{tokenCount}</p>
            <p className="text-sm text-mesa-muted">API Tokens</p>
          </div>
        </div>
        {BUILD_SHA && (
          <div className="flex items-center gap-2 pt-4 border-t border-mesa-border">
            <span className="text-sm text-mesa-muted">Build</span>
            <code className="text-sm text-mesa-muted bg-mesa-cream-dark px-2 py-0.5 rounded">{BUILD_SHA}</code>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopyBuild}>
              {buildCopied ? (
                <CheckIcon className="h-3.5 w-3.5 text-green-600" weight="fill" />
              ) : (
                <CopyIcon className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="border border-mesa-red/30 bg-mesa-red/5 p-6 space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-medium text-mesa-red">Danger Zone</h2>
          <p className="text-sm text-mesa-muted">
            Deleting your organization is permanent and will remove all repositories, API tokens, members, and
            invitations.
          </p>
        </div>
        <Button className="bg-mesa-red text-white hover:bg-mesa-red/90" onClick={handleOpenDelete}>
          <TrashIcon className="h-4 w-4" />
          Delete Organization
        </Button>
      </div>

      <Dialog open={isRenameDialogOpen} onOpenChange={handleCloseRenameDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update organization name</DialogTitle>
            <DialogDescription>This is the display name shown across Mesa.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={orgNameValue}
                onChange={(e) => setOrgNameValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && canRename && handleRenameOrg()}
              />
            </div>
            {renameError && <p className="text-sm text-mesa-red">{renameError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseRenameDialog} disabled={isRenaming}>
              Cancel
            </Button>
            <Button onClick={handleRenameOrg} disabled={!canRename || isRenaming}>
              {isRenaming && <SpinnerIcon className="size-4 mr-2 animate-spin" weight="fill" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={handleCloseDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting <span className="font-medium">{org.name}</span> will remove {repoCount}{' '}
              {repoCount === 1 ? 'repository' : 'repositories'}, all API tokens, all members, and all invitations. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-org-confirm">
                Type <span className="font-mono font-medium">{org.name}</span> to confirm
              </Label>
              <Input
                id="delete-org-confirm"
                placeholder={org.name}
                value={deleteConfirmValue}
                onChange={(e) => setDeleteConfirmValue(e.target.value)}
              />
            </div>
            {deleteError && <p className="text-sm text-mesa-red">{deleteError}</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDeleteDialog} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrg}
              disabled={deleteConfirmValue !== org.name || isDeleting}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
            >
              {isDeleting && <SpinnerIcon className="size-4 mr-2 animate-spin" weight="fill" />}
              Delete Organization
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
